/* eslint-disable react/jsx-sort-props */
/* eslint-disable prettier/prettier */
/* eslint-disable react/require-default-props */
/* eslint-disable camelcase */
import React, { useCallback, useState } from 'react';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import PersistentDataGrid from './PersistentDataGrid';

// =============================================================================
// PersistentServerSideDataGrid Component
// =============================================================================
const PersistentServerSideDataGrid = ({
  // Required props
  rows = [],
  totalRows = 0,
  loading = false,

  // Optional customization
  defaultPageSize = 10,
  rowHeight = 52,
  checkboxSelection = false,
  showFullScreenButton = false,
  showFilterButton = false,
  hideExportButton = false,
  disabledExport = false,
  quickFilter = false,

  // Export options
  exportOptions = {
    csvOptions: { allColumns: true },
    printOptions: { allColumns: true },
  },

  // Callbacks
  onPaginationChange,
  onSortChange,
  onFilterChange,
  onRowClick,
  onSelectionChange,
  onExportAllRows, // Optional: Async function to fetch all rows for export
  slotProps: incomingSlotProps,
  slots: incomingSlots,
  onResetView,

  // Additional DataGrid props
  ...dataGridProps
}) => {
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: defaultPageSize });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [isExporting, setIsExporting] = useState(false);

  // Handle custom export - fetch all data then trigger export
  const handleExport = useCallback(async () => {
    if (!onExportAllRows) return;

    setIsExporting(true);
    try {
      // Two supported flows:
      // 1) onExportAllRows returns an array of rows -> use grid CSV export
      // 2) onExportAllRows handles download itself (e.g., server-side CSV) -> return null/undefined
      const allRows = await onExportAllRows();

      if (Array.isArray(allRows)) {
        // Update grid with all rows temporarily
        apiRef.current.updateRows(allRows);

        // Small delay to ensure grid processes the updates
        await new Promise(resolve => {
          setTimeout(resolve, 100);
        });

        // Trigger CSV export using the grid's API
        apiRef.current.exportDataAsCsv({
          allColumns: true,
          ...exportOptions?.csvOptions,
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to export data:', err);
    } finally {
      setIsExporting(false);
    }
  }, [apiRef, onExportAllRows, exportOptions]);

  // Handlers
  const handlePaginationModelChange = useCallback(
    newModel => {
      if (isExporting) return;

      setPaginationModel(newModel);
      if (onPaginationChange) {
        onPaginationChange(newModel);
      }
    },
    [isExporting, onPaginationChange]
  );

  const handleSortModelChange = useCallback(
    newSortModel => {
      if (isExporting) return;

      setSortModel(newSortModel);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      if (onSortChange) {
        onSortChange(newSortModel);
      }
    },
    [isExporting, onSortChange]
  );

  const handleFilterModelChange = useCallback(
    newFilterModel => {
      setFilterModel(newFilterModel);
      setPaginationModel(prev => ({ ...prev, page: 0 }));
      if (onFilterChange) {
        onFilterChange(newFilterModel);
      }
    },
    [onFilterChange]
  );

  // Local reset: Resets server-side specific state (pagination, sort, filters)
  // Combined with PersistentDataGrid's reset (localStorage, column state, grid key)
  const handleResetView = useCallback(() => {
    // Reset server-side state back to defaults
    setPaginationModel({ page: 0, pageSize: defaultPageSize });
    setSortModel([]);
    setFilterModel({ items: [] });
    // Trigger data refetch with reset parameters
    if (onSortChange) {
      onSortChange([]);
    }
    if (onPaginationChange) {
      onPaginationChange({ page: 0, pageSize: defaultPageSize });
    }
    if (onFilterChange) {
      onFilterChange({ items: [] });
    }
    // Call parent onResetView callback if provided
    if (onResetView) {
      onResetView();
    }
  }, [defaultPageSize, onSortChange, onPaginationChange, onFilterChange, onResetView]);

  // Handler to restore server-side state from localStorage
  const handleServerSideStateRestore = useCallback(
    serverSideState => {
      if (serverSideState.paginationModel) {
        setPaginationModel(serverSideState.paginationModel);
        if (onPaginationChange) {
          onPaginationChange(serverSideState.paginationModel);
        }
      }
      if (serverSideState.sortModel) {
        setSortModel(serverSideState.sortModel);
        if (onSortChange) {
          onSortChange(serverSideState.sortModel);
        }
      }
      if (serverSideState.filterModel) {
        setFilterModel(serverSideState.filterModel);
        if (onFilterChange) {
          onFilterChange(serverSideState.filterModel);
        }
      }
    },
    [onPaginationChange, onSortChange, onFilterChange]
  );

  return (
      <PersistentDataGrid
        apiRef={apiRef}
        loading={loading}
        showFullScreenButton={showFullScreenButton}
        showFilterButton={showFilterButton}
        quickFilter={quickFilter}
        exportOptions={exportOptions}
        disabledExport={disabledExport}
        hideExportButton={hideExportButton}
        isExporting={isExporting}
        onExportClick={onExportAllRows ? handleExport : undefined}
        onReset={handleResetView}
        onServerSideStateRestore={handleServerSideStateRestore}
        serverSideState={{
          filterModel,
          paginationModel,
          sortModel,
        }}

        // Enable checkbox selection if specified
        checkboxSelection={checkboxSelection}

        // Rows
        disableRowSelectionOnClick
        onRowClick={onRowClick}
        onRowSelectionModelChange={onSelectionChange}
        rowCount={totalRows}
        rowHeight={rowHeight}
        rows={rows}

        // Server-side filtering - disable column menu filter option since we use custom filters
        disableColumnFilter
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}

        // Server-side sorting
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}

        // Server-side pagination
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}

        slotProps={{
          ...incomingSlotProps,
        }}
        slots={{
          ...incomingSlots,
        }}

        {...dataGridProps}
      />
  );
}

PersistentServerSideDataGrid.propTypes = {
  // Optional customization
  autoHeight: bool,
  checkboxSelection: bool,
  defaultPageSize: number,
  disabledExport: bool,
  exportOptions: shape({
    csvOptions: shape({}),
    printOptions: shape({}),
  }),
  hideExportButton: bool,
  loading: bool,
  onExportAllRows: func,
  onFilterChange: func,
  onPaginationChange: func,
  onResetView: func,
  onRowClick: func,
  onSelectionChange: func,
  onSortChange: func,
  quickFilter: bool,
  rowHeight: number,
  rows: arrayOf(shape({})),
  showFilterButton: bool,
  showFullScreenButton: bool,
  slotProps: shape({
    toolbar: shape({}),
  }),
  slots: shape({}),
  totalRows: number,
};

PersistentServerSideDataGrid.defaultProps = {
  quickFilter: false,
  showFilterButton: false,
};

export default PersistentServerSideDataGrid;
